"use client";

import { useEffect } from "react";
import posthog from "posthog-js";

/**
 * Global window-level error + unhandled-rejection listener. PostHog already
 * has `capture_exceptions: true`, but that path can fail silently on iOS for
 * cross-origin masked errors ("Script error" with no stack). This listener
 * adds a second capture path with explicit context so we ALWAYS get a record,
 * even if the original error was cross-origin opaque.
 *
 * Mount once at the root layout.
 */
export default function GlobalErrorListener() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      try {
        const err = event.error instanceof Error ? event.error : new Error(event.message || "window.error");
        posthog.captureException(err, {
          source: "window.onerror",
          message: event.message ?? null,
          filename: event.filename ?? null,
          lineno: event.lineno ?? null,
          colno: event.colno ?? null,
          pathname: window.location.pathname,
          ua: navigator.userAgent,
          cross_origin: event.message === "Script error.",
        });
      } catch {
        /* posthog not initialized */
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        const err =
          reason instanceof Error
            ? reason
            : new Error(typeof reason === "string" ? reason : "unhandled.rejection");
        posthog.captureException(err, {
          source: "unhandledrejection",
          reason_type: typeof reason,
          pathname: window.location.pathname,
          ua: navigator.userAgent,
        });
      } catch {
        /* ignore */
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
